import { useEffect, useMemo } from 'react';
import { useFormState, useFormApi } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import { hasMaterialDensity } from '@/lib/mappers';
import { ShipmentQuantityUnitField } from '@/components/common';
import { buildShipmentUnitRules } from '../outgoingShipmentsValidation';

/**
 * Props:
 *   materials  the form's material options, each carrying its units and display_unit id
 *   label      overrides the default wording; the add/edit form keeps its own
 *   disabled   locked while only the quantity may change
 */
export const ShipmentUnitField = ({ materials, label, disabled = false }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const material = useMemo(
    () => (materials ?? EMPTY_LIST).find((entry) => entry.id === String(values.materialId)),
    [materials, values.materialId],
  );

  const declarationUnit = material?.declarationUnit ?? '';
  // Without a conversion factor the server can't turn a working quantity into declaration.
  const workingUnit = hasMaterialDensity(material) ? (material?.workingUnit ?? '') : '';

  const options = useMemo(() => {
    const kinds = [
      [workingUnit, t('UNIT_WORKING')],
      [declarationUnit, t('UNIT_DECLARATION')],
    ];
    const seen = new Set();
    return kinds
      .filter(([unit]) => unit && !seen.has(unit) && seen.add(unit))
      .map(([unit, badgeText]) => ({ value: unit, label: unit, badgeText }));
  }, [workingUnit, declarationUnit, t]);

  const rules = useMemo(
    () => buildShipmentUnitRules(options.map((option) => option.value)),
    [options],
  );

  // Only once a material resolves — otherwise this would wipe Edit's prefilled display unit.
  const displayUnitId = material?.displayUnitId ?? '';
  useEffect(() => {
    if (!material) return;
    if (String(values.displayUnitId) !== String(displayUnitId)) {
      setValue('displayUnitId', displayUnitId);
    }
  }, [material, displayUnitId, values.displayUnitId, setValue]);

  useEffect(() => {
    if (!values.shipmentQuantityUnit && options.length === 1) {
      setValue('shipmentQuantityUnit', options[0].value);
    }
  }, [options, values.shipmentQuantityUnit, setValue]);

  return (
    <ShipmentQuantityUnitField options={options} label={label} disabled={disabled} rules={rules} />
  );
};
